// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 26-versioning-update-strategy.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api';

type Mode = 'apply' | 'canary';

const GENERIC_REFUSAL = 'The run could not be started.';

/**
 * The server's own words: `updater.run_in_progress` names the running Job,
 * `updater.not_enabled` explains that enabling it is an installer rerun.
 * Restating them here would drift from what the API actually said.
 */
function refusalMessage(body: unknown): string {
  const message = (body as { message?: unknown } | null)?.message;
  return typeof message === 'string' && message !== '' ? message : GENERIC_REFUSAL;
}

/**
 * The two org-admin actions docs/26 defers dashboard-triggered execution to:
 * **Preview update** runs a canary (discover, verify, preflight, provider plan,
 * change nothing) and **Apply now** runs what the schedule would have run.
 *
 * Both go through one contract op, so the org-admin gate, the single-flight
 * refusal, and the audit write are the server's — the page only decides what to
 * offer. While a run has reported a phase they are disabled rather than hidden;
 * a start that has not yet reported stays disabled on this card too, so the
 * 409 is not the only signal.
 *
 * Apply confirms first; preview does not. A preview changes nothing by
 * construction, and a confirmation on a no-op is the kind of friction that
 * teaches people to click through the ones that matter.
 */
export function UpdaterRunActions({
  runInFlight,
}: {
  /** A phase is live, so a second run would be refused anyway. */
  readonly runInFlight: boolean;
}): React.ReactElement {
  const trigger = api.health.triggerUpdaterRun.useMutation();
  const [confirming, setConfirming] = React.useState(false);
  const [startedMode, setStartedMode] = React.useState<Mode | null>(null);
  // Which action is in flight, so only that one says so.
  const [pendingMode, setPendingMode] = React.useState<Mode | null>(null);
  const [refusal, setRefusal] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (runInFlight) setStartedMode(null);
  }, [runInFlight]);

  const pending = trigger.isPending;
  const disabled = runInFlight || pending || startedMode !== null;

  const start = (mode: Mode): void => {
    // A fresh attempt clears the last one's outcome, so a success banner and a
    // refusal are never on the card together.
    setStartedMode(null);
    setRefusal(null);
    setPendingMode(mode);
    trigger.mutate(
      { body: { mode } },
      {
        // ts-rest resolves every status through this channel, so the 409
        // single-flight refusal docs/28 promises an admin will see arrives here
        // and not as an error. Without the status check it would render as a
        // started run.
        onSuccess: (result) => {
          setConfirming(false);
          if (result.status >= 300) {
            setRefusal(refusalMessage(result.body));
            return;
          }
          setStartedMode(mode);
        },
        onError: () => {
          setConfirming(false);
          setRefusal(GENERIC_REFUSAL);
        },
        onSettled: () => setPendingMode(null),
      },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          className="h-11 md:h-9"
          disabled={disabled}
          onClick={() => start('canary')}
        >
          {pendingMode === 'canary' ? 'Starting…' : 'Preview update'}
        </Button>
        <Button
          variant="default"
          className="h-11 md:h-9"
          disabled={disabled}
          onClick={() => setConfirming(true)}
        >
          Apply now
        </Button>
      </div>

      {runInFlight || startedMode !== null ? (
        <p className="text-xs text-muted-foreground">
          An update is running. The actions come back when it finishes.
        </p>
      ) : null}

      {startedMode !== null ? (
        <Alert>
          <AlertDescription>
            {startedMode === 'canary'
              ? 'Preview started. It verifies and plans the update without applying it; this page shows the result as it progresses.'
              : 'Update started. This page shows the result as it progresses.'}
          </AlertDescription>
        </Alert>
      ) : null}

      {refusal !== null ? (
        <Alert variant="destructive">
          <AlertDescription>{refusal}</AlertDescription>
        </Alert>
      ) : null}

      <GLDrawer
        variant="confirm"
        open={confirming}
        onOpenChange={setConfirming}
        mobileBreakpoint={768}
        title="Apply the update now"
        description="This applies the newest release to this install straight away, instead of waiting for the maintenance window. It runs the same steps the scheduled run would. The action is audited."
        preventDismiss={pending}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(false)} disabled={pending}>
              Cancel
            </Button>
            <Button variant="default" onClick={() => start('apply')} disabled={pending}>
              {pendingMode === 'apply' ? 'Starting…' : 'Apply now'}
            </Button>
          </>
        }
      >
        {null}
      </GLDrawer>
    </div>
  );
}
